import React, { useState, useEffect } from 'react';
import { IoShieldOutline, IoCheckmarkOutline, IoCopyOutline } from 'react-icons/io5';
import { toast, Toaster } from 'react-hot-toast';
import Countdown from 'react-countdown';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function TwoFactorLogin() {
    const { t } = useTranslation();
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [copiedStates, setCopiedStates] = useState({});
    const [showManualInput, setShowManualInput] = useState(false);
    const [manualCode, setManualCode] = useState('');
    const [countdownKey, setCountdownKey] = useState(0);
    const [countdownDate, setCountdownDate] = useState(null);

    useEffect(() => {
        // localStorage'dan countdown tarihini kontrol et
        const savedCountdownDate = localStorage.getItem('2fa-countdown-date');
        const now = Date.now();

        if (savedCountdownDate) {
            const savedDate = parseInt(savedCountdownDate);
            // Eğer kaydedilen tarih gelecekte ise, o tarihi kullan
            if (savedDate > now) {
                setCountdownDate(savedDate);
            } else {
                // Süre dolmuşsa yeni countdown başlat
                const newDate = now + 90000; // 1 dakika 30 saniye
                setCountdownDate(newDate);
                localStorage.setItem('2fa-countdown-date', newDate.toString());
            }
        } else {
            // İlk kez sayfa yükleniyorsa yeni countdown başlat
            const newDate = now + 90000; // 1 dakika 30 saniye
            setCountdownDate(newDate);
            localStorage.setItem('2fa-countdown-date', newDate.toString());
        }

        setCountdownKey(prev => prev + 1);
    }, []);

    const handleCodeChange = (index, value) => {
        if (value.length > 1) return; // Sadece tek karakter

        const newCode = [...verificationCode];
        newCode[index] = value;
        setVerificationCode(newCode);

        // Otomatik olarak bir sonraki input'a geç
        if (value && index < 5) {
            const nextInput = document.getElementById(`code-${index + 1}`);
            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        // Backspace tuşu ile önceki input'a geç
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            const prevInput = document.getElementById(`code-${index - 1}`);
            if (prevInput) {
                prevInput.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newCode = [...verificationCode];

        for (let i = 0; i < pastedData.length && i < 6; i++) {
            newCode[i] = pastedData[i];
        }

        setVerificationCode(newCode);

        // Son dolu input'a focus
        const lastFilledIndex = Math.min(pastedData.length - 1, 5);
        const nextInput = document.getElementById(`code-${lastFilledIndex}`);
        if (nextInput) {
            nextInput.focus();
        }
    };

    const handleSubmit = async () => {
        const codeString = showManualInput ? manualCode.trim() : verificationCode.join('');
        if (!codeString) {
            toast.error(showManualInput ? t('twoFactorLogin.toast.enterRecoveryCode') : t('twoFactorLogin.toast.enterVerificationCode'));
            return;
        }

        setIsLoading(true);
        try {
            let response;

            if (showManualInput) {
                // Kurtarma kodu girildi - ValidateBackupCode API'si kullan
                response = await api.validateBackupCode(codeString);

                if (response) {
                    // Kurtarma kodu başarılı - temp:token'i temizle ve login sayfasına yönlendir
                    localStorage.removeItem('temp:token');
                    localStorage.removeItem('2fa-countdown-date');

                    toast.success(t('twoFactorLogin.toast.recoveryCodeValidated'));
                    // Login sayfasına yönlendir
                    window.location.href = '/login';
                } else {
                    toast.error(t('twoFactorLogin.toast.recoveryCodeInvalid'));
                }
            } else {
                // 2FA kodu girildi - VerifyTwoFactor API'si kullan
                response = await api.verifyTwoFactor(codeString);

                if (response && response.isTwoFactorEnabled === true && response.token) {
                    // Başarılı - temp:token'i temizle ve yeni token'i kaydet
                    localStorage.removeItem('temp:token');
                    localStorage.setItem('auth:token', response.token);

                    // Kullanıcıyı tam olarak giriş yapmış olarak işaretle
                    localStorage.setItem('auth:user', JSON.stringify({ isAuthenticated: true, needs2FA: false }));

                    // localStorage'dan countdown tarihini temizle
                    localStorage.removeItem('2fa-countdown-date');

                    toast.success(t('twoFactorLogin.toast.loginSuccessful'));
                    // Ana sayfaya yönlendir
                    window.location.href = '/';
                } else {
                    toast.error(t('twoFactorLogin.toast.verificationCodeInvalid'));
                }
            }
        } catch (error) {
            console.error('2FA doğrulama hatası:', error);
            toast.error(t('twoFactorLogin.toast.verificationFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    // Countdown renderer
    const countdownRenderer = ({ minutes, seconds, completed }) => {
        if (completed) {
            return <span style={{ color: '#ef4444' }}>{t('twoFactorLogin.timeExpired')}</span>;
        } else {
            return (
                <span style={{ color: '#7C3AED', fontWeight: '600' }}>
                    {minutes}:{seconds.toString().padStart(2, '0')}
                </span>
            );
        }
    };

    return (
        <div className="two-factor-login-container">
            <div className="container" style={{ paddingTop: '140px', paddingBottom: '80px' }}>
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="row gx-lg-5 align-items-center login-content">
                            {/* Sol taraf - Görsel */}
                            <div className="col-lg-6 ps-lg-0">
                                <div className="login-form-image-content position-relative">
                                    <div className="d-flex flex-column justify-content-between h-100">
                                        <div>
                                            <img src="/assets/images/nordex-post-2.png" alt="" width="210px" className='login-form-image-content-logo' />
                                        </div>
                                        <div>
                                            <h1 className='login-form-image-content-title'>
                                                {t('twoFactorLogin.title')}
                                            </h1>
                                            <p className='login-form-image-content-subtitle'>
                                                {t('twoFactorLogin.subtitle')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sağ taraf - Form */}
                            <div className="col-lg-6">
                                <div className='d-flex align-items-center mb-lg-3 gap-2'>
                                    <IoShieldOutline size={28} color='#7C3AED' />
                                    <h3 className='mb-0'>{t('twoFactorLogin.verificationTitle')}</h3>
                                </div>
                                <div className='mb-5'>
                                    <p className='mb-0 login-subtitle'>
                                        {t('twoFactorLogin.verificationSubtitle')}
                                    </p>
                                </div>

                                {/* Timer */}
                                <div className="text-center mb-4">
                                    <div className="timer-display">
                                        {t('twoFactorLogin.remainingTime')} {countdownDate && (
                                            <Countdown
                                                key={countdownKey}
                                                date={countdownDate}
                                                renderer={countdownRenderer}
                                                onComplete={() => {
                                                    toast.error(t('twoFactorLogin.toast.timeExpired'));
                                                    // localStorage'dan countdown tarihini temizle
                                                    localStorage.removeItem('2fa-countdown-date');
                                                    // Tüm auth verilerini temizle
                                                    localStorage.removeItem('auth:token');
                                                    localStorage.removeItem('auth:user');
                                                    localStorage.removeItem('temp:token');
                                                    // Login sayfasına yönlendir
                                                    window.location.href = '/login';
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Code Input */}
                                {showManualInput ? (
                                    <div className="mb-4">
                                        <input
                                            type="text"
                                            value={manualCode}
                                            onChange={(e) => setManualCode(e.target.value)}
                                            placeholder={t('twoFactorLogin.enterRecoveryCode')}
                                            className="form-control manual-code-input"
                                            autoComplete="off"
                                        />
                                    </div>
                                ) : (
                                    <div className="code-input-container mb-4">
                                        {verificationCode.map((digit, index) => (
                                            <input
                                                key={index}
                                                id={`code-${index}`}
                                                type="text"
                                                value={digit}
                                                onChange={(e) => handleCodeChange(index, e.target.value.replace(/\D/g, ''))}
                                                onKeyDown={(e) => handleKeyDown(index, e)}
                                                onPaste={handlePaste}
                                                className="code-input"
                                                maxLength={1}
                                                autoComplete="off"
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Submit Button */}
                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading || (showManualInput ? manualCode.trim() === '' : verificationCode.join('').length !== 6)}
                                    className="btn-primary w-100 btn-submit-2fa"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="spinner"></div>
                                            {t('twoFactorLogin.verifying')}
                                        </>
                                    ) : (
                                        t('twoFactorLogin.login')
                                    )}
                                </button>

                                {/* Help Link */}
                                <div className="text-center mt-4">
                                    <button
                                        onClick={() => setShowManualInput(!showManualInput)}
                                        className="help-link"
                                        style={{ background: 'none', border: 'none', color: 'var(--primary)', textDecoration: 'none', cursor: 'pointer' }}
                                    >
                                        {showManualInput ? t('twoFactorLogin.switchToCode') : t('twoFactorLogin.switchToManual')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                }}
            />
        </div>
    );
}
